import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiServiceService } from './api-service.service';
import { HttpParams } from '@angular/common/http';
import {
  ApiEnvelope,
  CategoryFeesSummaryPayload,
  DashboardSummaryPayload,
  PaymentChannelsPayload,
  ReportsAnalyticsPayload,
  RevenueSummaryPayload,
  RevenueSummaryPeriod,
  PendingWorkflowApprovalsPayload,
  RevenueTrendPayload,
  VendorRegistrationTrendPayload,
  VendorsByCategoryPayload,
  VendorsByMdaPayload,
  VendorsSummaryPayload,
} from './metrics.models';

@Injectable({ providedIn: 'root' })
export class MetricsService {
  constructor(private readonly api: ApiServiceService) {}

  private unwrap<T>(res: ApiEnvelope<T>, label: string): T {
    const payload = res?.data?.data;
    if (!payload) {
      throw new Error(`${label} payload is missing.`);
    }
    return payload;
  }

  getVendorsSummary(): Observable<VendorsSummaryPayload> {
    return this.api
      .get('/v1/metrics/vendors/summary')
      .pipe(map((res: ApiEnvelope<VendorsSummaryPayload>) => this.unwrap(res, 'Vendors summary')));
  }

  getDashboardSummary(): Observable<DashboardSummaryPayload> {
    return this.api
      .get('/v1/metrics/dashboard-summary')
      .pipe(map((res: ApiEnvelope<DashboardSummaryPayload>) => this.unwrap(res, 'Dashboard summary')));
  }

  getVendorRegistrationTrend(months = 12): Observable<VendorRegistrationTrendPayload> {
    return this.api
      .get(`/v1/metrics/vendors/registration-trend?months=${months}`)
      .pipe(
        map((res: ApiEnvelope<VendorRegistrationTrendPayload>) =>
          this.unwrap(res, 'Vendor registration trend'),
        ),
      );
  }

  getRevenueSummary(period: RevenueSummaryPeriod = 'month'): Observable<RevenueSummaryPayload> {
    return this.api
      .get(`/v1/metrics/revenue/summary?period=${period}`)
      .pipe(map((res: ApiEnvelope<RevenueSummaryPayload>) => this.unwrap(res, 'Revenue summary')));
  }

  getRevenueTrend(months = 12): Observable<RevenueTrendPayload> {
    return this.api
      .get(`/v1/metrics/revenue/trend?months=${months}`)
      .pipe(
        map((res: ApiEnvelope<RevenueTrendPayload>) => this.unwrap(res, 'Revenue trend')),
      );
  }

  getVendorsByCategory(): Observable<VendorsByCategoryPayload> {
    return this.api
      .get('/v1/metrics/vendors/by-category')
      .pipe(
        map((res: ApiEnvelope<VendorsByCategoryPayload>) =>
          this.unwrap(res, 'Vendors by category'),
        ),
      );
  }

  getVendorsByMda(): Observable<VendorsByMdaPayload> {
    return this.api
      .get('/v1/metrics/vendors/by-mda')
      .pipe(map((res: ApiEnvelope<VendorsByMdaPayload>) => this.unwrap(res, 'Vendors by MDA')));
  }

  getCategoryFeesSummary(): Observable<CategoryFeesSummaryPayload> {
    return this.api
      .get('/v1/metrics/category-fees/summary')
      .pipe(
        map((res: ApiEnvelope<CategoryFeesSummaryPayload>) =>
          this.unwrap(res, 'Category fees summary'),
        ),
      );
  }

  getReportsAnalytics(filters: {
    startDate?: string;
    endDate?: string;
    mdaId?: string;
    categoryId?: string;
    paymentChannel?: string;
  }): Observable<ReportsAnalyticsPayload> {
    const hasStart = Boolean(filters?.startDate);
    const hasEnd = Boolean(filters?.endDate);
    const hasDates = hasStart && hasEnd;

    let params = new HttpParams();
    if (hasDates) {
      params = params.set('startDate', String(filters.startDate));
      params = params.set('endDate', String(filters.endDate));
    }
    if (filters?.mdaId) params = params.set('mdaId', String(filters.mdaId));
    if (filters?.categoryId) params = params.set('categoryId', String(filters.categoryId));
    if (filters?.paymentChannel) params = params.set('paymentChannel', String(filters.paymentChannel));

    return this.api
      .get('/v1/metrics/reports/analytics', { params })
      .pipe(map((res: ApiEnvelope<ReportsAnalyticsPayload>) => this.unwrap(res, 'Reports analytics')));
  }

  getPaymentChannels(): Observable<PaymentChannelsPayload> {
    return this.api
      .get('/metrics/payments/channels')
      .pipe(
        map((res: ApiEnvelope<PaymentChannelsPayload>) => this.unwrap(res, 'Payment channels')),
        map((payload) => ({
          channels: Array.isArray(payload?.channels) ? payload.channels : [],
        })),
      );
  }

  getPendingWorkflowApprovals(): Observable<PendingWorkflowApprovalsPayload> {
    return this.api
      .get('/v1/metrics/workflows/pending-approvals')
      .pipe(
        map((res: ApiEnvelope<PendingWorkflowApprovalsPayload>) =>
          this.unwrap(res, 'Pending approvals'),
        ),
        map((payload) => ({
          pending: Number(payload?.pending ?? 0),
          byWorkflow: Array.isArray(payload?.byWorkflow) ? payload.byWorkflow : [],
        })),
      );
  }
}
